import { FC, useState } from "react";

import { FormType } from "@simplyk/common";

import { useLiveFormEditorContext } from "../common/LiveFormEditorContext";

import { useLiveFormEditorExit } from "./useLiveFormEditorExit";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import {
  LiveEditorTopBar,
  EditorNavMobileAction,
  EditorNavButtonElement,
} from "@/components/LiveEditorElements/LiveEditorTopBar/LiveEditorTopBar";
import { LIVE_FORM_Z_INDEX } from "@/components/LiveEditorElements/liveFormZIndex";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { useDeleteForm } from "@/features/Campaigns/CampaignList/hooks/useDeleteForm";
import { Expand, Menu } from "@/icons/outlined";

export const LiveFormTopBar: FC = () => {
  const { t } = useTranslate();
  const {
    isOnboarding,
    totalNumberOfSteps,
    currentStepIndex,
    currentStepKey,
    editOrCreateMode,
    setIsNavigationOpen,
    shareFormModalState,
    saveCurrentStep,
    isFormDirty,
    setIsMobilePreviewOpen,
    isMobilePreviewOpen,
    stepProgress,
    apiFormObject,
    formCategory,
    formType,
    editorMode,
  } = useLiveFormEditorContext();
  const { exitLiveFormEditor, exitAfterFormDeletion } = useLiveFormEditorExit();
  const { isSmallScreen } = useMediaQuery();

  const [isSaveAndShareLoading, setIsSaveAndShareLoading] = useState(false);
  const [isSaveAndExitLoading, setIsSaveAndExitLoading] = useState(false);
  const deleteForm = useDeleteForm();
  const isCreateMode = editOrCreateMode === "create";
  const isEditMode = editOrCreateMode === "edit";

  const handleSaveAndShare = async () => {
    setIsSaveAndShareLoading(true);
    try {
      if (isFormDirty) {
        const success = await saveCurrentStep.current?.();
        if (!success) {
          return; // Validation failed, abort
        }
      }
      shareFormModalState.openModal();
    } finally {
      setIsSaveAndShareLoading(false);
    }
  };

  const handleSaveAndExit = async () => {
    setIsSaveAndExitLoading(true);
    try {
      if (isFormDirty) {
        const success = await saveCurrentStep.current?.();
        if (!success) {
          return; // Validation failed, abort
        }
      }
      exitLiveFormEditor();
    } finally {
      setIsSaveAndExitLoading(false);
    }
  };

  const handleDeleteFormAndExit = async () => {
    const deleted = await deleteForm({
      id: apiFormObject.id,
      formType: formType === "ticketing" ? FormType.Ticketing : FormType.DonationForm,
      formCategory,
      onlyDraft: true,
    });
    if (deleted) {
      await exitAfterFormDeletion();
    }
  };

  const progressMode = (() => {
    if (isSmallScreen) {
      return "hide"; // small screen, the progress bar is in the footer
    }
    if (isCreateMode) {
      return isOnboarding ? "only-progress-bar" : "with-progress-bar";
    }

    return "hide"; // for edit mode, the progress bar is not needed
  })();

  // If the user has not made any changes and has never passed the title step, just delete the form on exit
  const exitButtonMode =
    !isFormDirty && stepProgress.reached === 0 && isCreateMode ? "delete-form-and-exit" : "save-and-exit";
  const exitButtonLabel =
    exitButtonMode === "delete-form-and-exit"
      ? t("dashboard", "liveFormEditor.topBar.exit")
      : t("dashboard", "liveFormEditor.topBar.saveAndExit");

  const handleExitAction = exitButtonMode === "delete-form-and-exit" ? handleDeleteFormAndExit : handleSaveAndExit;
  const exitActionDataTest =
    exitButtonMode === "delete-form-and-exit" ? "delete-form-and-exit-button" : "save-and-exit-button";

  return (
    <LiveEditorTopBar
      progress={{ currentStepIndex, totalNumberOfSteps, mode: progressMode }}
      desktop={{
        leftButtons: isEditMode
          ? [
              // Toggle navigation button (desktop)
              <DashboardIconButton
                key="navigation-toggle"
                onClick={() => {
                  setIsNavigationOpen(true);
                }}
                vibe="neutral"
                variant="outlined"
                data-test="sidebar-toggle-button"
              >
                <Expand />
              </DashboardIconButton>,
            ]
          : undefined,
        rightButtons: isEditMode
          ? ([
              // Save and share button (desktop)
              currentStepKey !== "review" && (
                <DashboardButton
                  key="save-share"
                  onClick={handleSaveAndShare}
                  vibe="neutral"
                  variant="outlined"
                  isLoading={isSaveAndShareLoading}
                  data-test="save-and-share-button"
                >
                  {t("dashboard", "liveFormEditor.topBar.saveAndShare")}
                </DashboardButton>
              ),
              // Save and exit button (desktop)
              editorMode !== "fundraiserToken" && (
                <DashboardButton
                  key="save-exit"
                  onClick={handleSaveAndExit}
                  vibe="neutral"
                  variant="outlined"
                  isLoading={isSaveAndExitLoading}
                  data-test="save-and-exit-button"
                >
                  {t("dashboard", "liveFormEditor.topBar.saveAndExit")}
                </DashboardButton>
              ),
            ].filter(Boolean) as EditorNavButtonElement[])
          : isCreateMode && !isOnboarding
            ? [
                // Exit button (desktop)
                <DashboardButton
                  key="exit"
                  onClick={handleExitAction}
                  data-test={exitActionDataTest}
                  vibe="neutral"
                  variant="outlined"
                  isLoading={isSaveAndExitLoading}
                >
                  {exitButtonLabel}
                </DashboardButton>,
              ]
            : undefined,
        showLogo: isCreateMode && !isOnboarding,
        onLogoClick: () => {
          void handleExitAction();
        },
      }}
      mobile={{
        leftAction:
          !isOnboarding && isEditMode
            ? ({
                ariaLabel: "Open navigation",
                icon: <Menu />,
                onClick: () => {
                  setIsNavigationOpen(true);
                },
                "data-test": "mobile-sidebar-toggle",
              } satisfies EditorNavMobileAction)
            : undefined,
        rightAction: !(isOnboarding && isCreateMode)
          ? ({
              ariaLabel: exitButtonLabel,
              label: exitButtonLabel,
              onClick: handleExitAction,
              isLoading: isSaveAndExitLoading,
              "data-test": exitActionDataTest,
            } satisfies EditorNavMobileAction)
          : undefined,
        onPreviewClick: () => {
          setIsMobilePreviewOpen(true);
        },
      }}
      mobilePreview={{
        isOpen: isMobilePreviewOpen,
        onBackToEditingClick: () => {
          setIsMobilePreviewOpen(false);
        },
      }}
      zIndex={LIVE_FORM_Z_INDEX.TOP_BAR}
      data-test="live-form-top-bar"
    />
  );
};
